import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Container,
  Chip,
  Button,
  Grid,
  LinearProgress,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  MenuItem,
} from "@mui/material";
import bkstApi from "../../api";
import OrderDetails from "../component/OrderDetails";
import OrderFulfillment from "../component/OrderFulfillment";
import BakerOrderPayment from "../component/BakerOrderPayment";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Link as RouterLink } from "react-router-dom";
import { Alert } from "@mui/material";
import moment from "moment";
import { useNavigate } from "react-router";
import { hasManagerAccess, capitalize, mergeItemProgress } from "../../common/util";
import { canAccessPath, canEditPath } from "../../common/access-control";
import BakerMktOrderPolicy from "../component/BakerMktOrderPolicy";
import { UserContext } from "../../context/UserContext";
import OrderChat from "../component/OrderChat";
import { withAppendedItemImage } from "../../common/chat/useAddChatImageToItem";
import { useLocation } from "react-router-dom";
import OrderPrint from "../../baker/screen/OrderPrint";
import Review from "../../review/component/Review";
import PendingReviewOrder from "./PendingReviewOrder";
import KitchenInstructions from "../component/KitchenInstructions";
import OrderProgress from "../component/OrderProgress";
import { ExpandMoreOutlined } from "@mui/icons-material";
import UserAvatar from "../../common/UserAvatar";
import OrderReceiptPrint from "../../baker/screen/OrderReceiptPrint";
import { Timeline, TimelineContent, TimelineConnector, TimelineSeparator } from "@mui/lab";
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import StorePolicy from "../../common/component/StorePolicy";
import SendReceipt from "../component/SendReceipt";
import Customer from "../component/Customer";

export default function Order(props) {
  const { user } = useContext(UserContext);
  let location = useLocation();

  const navigate = useNavigate();

  const { orderId } = useParams();
  const [order, setOrder] = useState();

  const [history, setHistory] = useState("");
  const [reviewExpanded, setReviewExpanded] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const getPageTitle = (order) => {
    let orderPageTitle = "Order Details";
    if (order) {
      if (order.paymentType === "external") {
        orderPageTitle = `${capitalize(order.src)} Order`;
      } else {
        orderPageTitle = [
          `${capitalize(order.fulfillment.type)} Order`,
          moment(order.fulfillment.date).format("ddd MMMM D, YYYY"),
        ]
          .filter(Boolean)
          .join(" • ");
      }
    }

    return orderPageTitle;
  };

  useEffect(() => {
    setOrder();
    bkstApi(`/my/order/${orderId}`)
      .then((res) => {
        document.title = getPageTitle(res.data);
        setOrder(res.data);
      })
      .catch((error) => {
        navigate(`/dashboard`);
      });
  }, [orderId, location.key]);

  useEffect(() => {
    // Check for #review hash on mount and when order is loaded
    const checkHash = () => {
      if (window.location.hash === "#review") {
        setReviewExpanded(true);
        setTimeout(() => {
          const reviewElement = document.getElementById("order-review");
          if (reviewElement) {
            reviewElement.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 500);
      }
    };

    if (order) {
      checkHash();
    }

    // Also listen for hash change
    window.addEventListener("hashchange", checkHash);
    return () => window.removeEventListener("hashchange", checkHash);
  }, [order]);

  const reloadOrder = () =>
    bkstApi(`/my/order/${orderId}`).then((res) => {
      document.title = getPageTitle(res.data);
      setOrder(res.data);
    });

  const printReceipt = () => {
    bkstApi
      .post(`/my/printer/${user.config.receiptPrinterId}/job?orderId=${orderId}`)
      .then((res) => {});
  };

  const getHistory = () => {
    return bkstApi(`/my/order/${orderId}/history`).then((res) => {
      let arr = [];
      for (let o of res.data.history) {
        if (!o.updTs) {
          o.updTs = o.ts._seconds * 1000;
        }
        arr.push(o);
      }
      arr.sort((o1, o2) => +o2.updTs - +o1.updTs);

      setHistory(arr);
    });
  };

  if (!order) {
    return <LinearProgress />;
  } else {
    let isManager = hasManagerAccess(user.role) || user.role === "kitchen leader";
    const orderPath = `/order/${orderId}`;

    let showEditButton =
      canEditPath(user, orderPath) &&
      canAccessPath(user, `${orderPath}/change`) &&
      order.status === "confirmed";
    let showChat =
      (isManager && order.src === "direct" && order.paymentType !== "offline") ||
      (isManager &&
        order.src === "mkt" &&
        (order.status === "confirmed" || order.status === "fulfilled"));

    const isReadOnly = order.paymentType === "external";
    if (isReadOnly) {
      showEditButton = false;
      showChat = false;
      isManager = false;
    }

    const orderDetailsWidget = (
      <Box mt={2}>
        <Grid container alignItems="center" justifyContent="flex-end">
          <Grid item xs={6}>
            <Typography variant="overline" color="textSecondary">
              Order Details
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Box
              sx={{ display: "flex", gap: "4px", alignItems: "center", justifyContent: "flex-end" }}
            >
              {showChat && (
                <OrderChat
                  order={order}
                  onImageAdded={(itemIndex, url) => {
                    setOrder((prev) => {
                      if (!prev?.items) return prev;
                      return { ...prev, items: withAppendedItemImage(prev.items, itemIndex, url) };
                    });
                  }}
                />
              )}
              {/*showAddOnButton && (
                <IconButton size="small" component={RouterLink} to={`/order/${orderId}/create-invoice`}>
                  <AddShoppingCartOutlinedIcon />
                </IconButton>
              )*/}
              {showEditButton && (
                <IconButton size="small" component={RouterLink} to={`/order/${orderId}/change`}>
                  <EditOutlinedIcon />
                </IconButton>
              )}
              {isManager && order.paymentType === "deferred" && (
                <>
                  <IconButton
                    size="small"
                    onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                    title="More options"
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={() => setMenuAnchorEl(null)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem
                      sx={{ padding: "20px" }}
                      component={RouterLink}
                      to="/create"
                      state={{
                        customer: order.customer,
                        items: order.items.map(({ progress, ...item }) => item),
                        fulfillment: order.fulfillment,
                        settings: order.settings,
                      }}
                      onClick={() => setMenuAnchorEl(null)}
                    >
                      <ContentCopyIcon sx={{ mr: 1, fontSize: 20 }} />
                      Duplicate Order
                    </MenuItem>
                  </Menu>{" "}
                </>
              )}
            </Box>
          </Grid>
        </Grid>
        <OrderDetails
          order={order}
          allowDownload={true}
          onItemProgressChange={
            isManager && order.status === "confirmed"
              ? ({ items }) =>
                  setOrder((prev) => ({ ...prev, items: mergeItemProgress(prev.items, items) }))
              : undefined
          }
        />
        <Box sx={{ borderTop: "2px solid #EEE" }}>
          <OrderFulfillment
            src={order.src}
            type={order.fulfillment.type}
            order={order.fulfillment}
          />
        </Box>
      </Box>
    );

    const kitchenWidget = (
      <Box my={6}>
        <Grid container style={{ marginBottom: "5px", alignItems: "center" }}>
          <Grid item xs={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
              {order.uid && <UserAvatar uid={order.uid} />}
              <Typography variant="overline" color="textSecondary">
                PRODUCTION
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            {order.status === "confirmed" && (
              <Box
                sx={{
                  display: "flex",
                  gap: "4px",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                {isManager && <OrderProgress order={order} size={22} />}
                <OrderPrint order={order} />
                {isManager && (
                  <IconButton
                    size="small"
                    component={RouterLink}
                    to={`/order/${orderId}/kitchen/edit`}
                  >
                    <EditOutlinedIcon />
                  </IconButton>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
        <KitchenInstructions data={order.kitchen} />
      </Box>
    );

    let content = <Box />;
    if (order.status === "pending") {
      content = (
        <Box>
          <Box mt={2} mb={6}>
            <OrderDetails order={order} allowDownload={true} />
            <Box sx={{ borderTop: "2px solid #EEE" }}>
              <OrderFulfillment type={order.fulfillment.type} order={order.fulfillment} />
            </Box>
          </Box>
        </Box>
      );
    } else if (order.status === "canceled" && order.src === "mkt") {
      content = (
        <Box>
          <Alert severity="error">This order has been canceled</Alert>
          <Box mt={2} mb={6}>
            <OrderDetails order={order} />
          </Box>
        </Box>
      );
    } else if (order.status === "confirmed") {
      content = (
        <Box>
          {orderDetailsWidget}
          <Box my={4}>
            <BakerOrderPayment order={order} onPaymentSuccess={reloadOrder} />
          </Box>
          {kitchenWidget}
        </Box>
      );
    } else if (order.status === "fulfilled") {
      content = (
        <Box>
          {orderDetailsWidget}
          <Box my={4}>
            <BakerOrderPayment order={order} onPaymentSuccess={reloadOrder} />
          </Box>
          {kitchenWidget}
        </Box>
      );
    } else if (order.status === "completed" || order.status === "canceled") {
      content = (
        <Box>
          {orderDetailsWidget}
          <Box my={4}>
            <BakerOrderPayment order={order} onPaymentSuccess={reloadOrder} />
          </Box>
          {kitchenWidget}
        </Box>
      );
    }

    if (order.status === "pending_review") {
      return <PendingReviewOrder order={order} />;
    } else {
      let orderPageTitle = getPageTitle(order);

      return (
        <Container maxWidth="md">
          <Box my={2}>
            {(order.status === "fulfilled" || order.status === "completed") && (
              <Alert severity="success">This order has been completed!</Alert>
            )}
            {order.status === "canceled" && (
              <Alert severity="error">This order has been canceled</Alert>
            )}
            {order.paymentType === "offline" && (
              <Alert severity="warning">
                Orders without a card on file lack automated processing. The system will not track
                payments, support in-app chat, or trigger customer notifications (confirmations,
                reminders, or reviews)
              </Alert>
            )}
            {/*order.status === "confirmed" && (hasManagerAccess(user.role) || isClerk) && (
              <Box mt={2}>
                <MarkOrderCompleted order={order} />
              </Box>
            )*/}
          </Box>
          <Box py={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
              <Typography variant="h1" component="span">
                {orderPageTitle}
              </Typography>
              {order.autoQuote && <AutoAwesomeOutlinedIcon sx={{ color: "#999" }} />}
            </Box>
            {isManager && (
              <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
                Manage all aspects of this order, including communication with customer, payments,
                add-ons, order updates & more..
              </Typography>
            )}
            {isReadOnly && (
              <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
                This is a read-only order that was placed outside of BakeStreet. Editing, messaging,
                and other actions are not available.
              </Typography>
            )}
          </Box>
          <Customer customer={order.customer} options={{ linkToProfile: !isReadOnly }} />
          <Box>{content}</Box>
          {order.src === "mkt" && (
            <Box mt={2} mb={6}>
              <Typography variant="caption" color="textSecondary" gutterBottom>
                BAKESTREET'S POLICIES
              </Typography>
              <Box p={2} sx={{ border: "1px solid #DDD" }}>
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    This is a BakeStreet marketplace order. The following policies are applicable to
                    you.
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    ---
                  </Typography>
                </Box>
                <BakerMktOrderPolicy />
              </Box>
            </Box>
          )}
          <Box mt={2} mb={6}>
            {order.src === "direct" && order.paymentType !== "offline" && (
              <Accordion elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                  Store Policy
                </AccordionSummary>
                <AccordionDetails>
                  <Box pt={4} sx={{ borderTop: "1px solid #DDD" }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        This policy is available on your website footer. It is also shown to
                        customers when they checkout and also on their order page.
                      </Typography>
                    </Box>
                    <StorePolicy />
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
            {order.reqId && hasManagerAccess(user.role) && (
              <Accordion elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreOutlined />}>Quote</AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ borderTop: "1px solid #DDD" }}>
                    <Box my={4}>
                      <Button
                        component={RouterLink}
                        variant="contained"
                        color="secondary"
                        fullWidth
                        to={`/quote/${order.reqId}`}
                      >
                        View Quote
                      </Button>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
            {!isReadOnly && (
              <Accordion elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreOutlined />}>Receipt</AccordionSummary>
                <AccordionDetails>
                  <Box pt={4} sx={{ borderTop: "1px solid #DDD" }} textAlign="center">
                    <Box sx={{ display: "flex", gap: "1rem" }}>
                      {user.config.receiptPrinterId && (
                        <Button
                          fullWidth
                          size="large"
                          color="secondary"
                          variant="outlined"
                          onClick={() => printReceipt()}
                        >
                          Print
                        </Button>
                      )}
                      <SendReceipt
                        fullWidth
                        orderId={orderId}
                        mode="email"
                        email={order.customer?.email}
                      >
                        <Button fullWidth size="large" color="secondary" variant="outlined">
                          Email
                        </Button>
                      </SendReceipt>
                      <SendReceipt
                        fullWidth
                        orderId={orderId}
                        mode="sms"
                        phone={order.customer?.phone}
                      >
                        <Button
                          fullWidth
                          size="large"
                          color="secondary"
                          variant="contained"
                          disableElevation
                        >
                          Text
                        </Button>
                      </SendReceipt>
                    </Box>
                    <Box my={1}>
                      <Typography variant="caption" color="textSecondary">
                        Ideal for a quick itemized summary of the payment details
                      </Typography>
                    </Box>
                    <Box mt={4} textAlign="center">
                      <OrderReceiptPrint order={order} />
                      <Box my={1}>
                        <Typography variant="caption" color="textSecondary">
                          Print a comprehensive receipt with all the order details - ideal for
                          large, future orders with many customizations. Prints on an A4 page or
                          larger.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
            {isManager && order.src === "direct" && (
              <Accordion
                elevation={0}
                onChange={(e, expanded) => {
                  if (expanded) {
                    getHistory();
                  }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreOutlined />}>History</AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <Box>
                    {history && (
                      <Timeline
                        sx={{
                          [`& .${timelineItemClasses.root}:before`]: {
                            flex: 0,
                            padding: 0,
                          },
                        }}
                      >
                        {history.map((o) => {
                          return (
                            <TimelineItem>
                              <TimelineSeparator>
                                <Avatar
                                  src={`https://storage.googleapis.com/bakestreet-f55a0.appspot.com/img/u/${o.uid}.webp`}
                                />
                                <TimelineConnector />
                                <TimelineConnector />
                              </TimelineSeparator>
                              <TimelineContent ml={1} mb={4} sx={{ backgroundColor: "#F7F7F7" }}>
                                <Box mb={1}>
                                  <Typography variant="body2" display="block">
                                    {moment(o.updTs).format("LLLL")}
                                  </Typography>
                                  <Chip size="small" label={o.status} />
                                  <Box my={2}>
                                    <OrderDetails order={o} />
                                    <Box sx={{ borderTop: "2px solid #EEE" }}>
                                      <OrderFulfillment
                                        type={o.fulfillment.type}
                                        order={o.fulfillment}
                                        hide3rdPartyStatus={true}
                                      />
                                    </Box>
                                    <Box my={2}>
                                      <BakerOrderPayment
                                        order={order}
                                        onPaymentSuccess={reloadOrder}
                                      />
                                    </Box>
                                    {o.payment.dueDate && (
                                      <Box my={2}>
                                        <Alert severity="info">
                                          Balance Due on {moment(o.payment.dueDate).format("MMM D")}
                                        </Alert>
                                      </Box>
                                    )}
                                  </Box>
                                  {o.kitchen?.notes && (
                                    <Box my={2}>
                                      <Typography variant="overline" color="textSecondary">
                                        PRODUCTION
                                      </Typography>
                                      <KitchenInstructions data={o.kitchen} />
                                    </Box>
                                  )}
                                </Box>
                              </TimelineContent>
                            </TimelineItem>
                          );
                        })}
                      </Timeline>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
            {isManager && order.review && (
              <Accordion
                id="order-review"
                elevation={0}
                expanded={reviewExpanded}
                onChange={(e, expanded) => setReviewExpanded(expanded)}
              >
                <AccordionSummary expandIcon={<ExpandMoreOutlined />}>Review</AccordionSummary>
                <AccordionDetails>
                  <Review data={order.review} />
                </AccordionDetails>
              </Accordion>
            )}
          </Box>

          {canEditPath(user, orderPath) &&
            !isReadOnly &&
            order.status === "confirmed" &&
            order.src === "direct" && (
            <Box align="center" mt={2} mb={6}>
              <Button
                color="error"
                variant="outlined"
                fullWidth
                to={`/order/${order.id}/cancel`}
                component={RouterLink}
              >
                Cancel This Order
              </Button>
              <Typography
                mt={2}
                variant="body2"
                color="textSecondary"
              >{`This order was confirmed on ${moment(order.ts._seconds * 1000).format(
                "ddd, MMM DD, YYYY h:mm a"
              )} (${moment(order.ts._seconds * 1000).fromNow()})`}</Typography>
            </Box>
          )}
        </Container>
      );
    }
  }
}
